import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const ChannelProgramsPage = () => {
  const [channelProgramData, setChannelProgramData] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [formData, setFormData] = useState({
    name: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editChannelProgramId, setEditChannelProgramId] = useState(null);

  const navigate = useNavigate();
  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => "Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("actions", {
        header: () => "Actions",
        cell: (info) => (
          <div className="">
            <button
              onClick={() => handleEdit(info.row.original)}
              className="btn btn-primary btn-xs"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(info.row.original._id)}
              className="btn btn-error btn-xs"
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: channelProgramData,
    columns,
    state: {
      globalFilter: searchFilter,
    },
    onGlobalFilterChange: setSearchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getChannelProgramList = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      const response = await axios.get(`/channel_programs`, { headers });
      setChannelProgramData(response.data.data);
    } catch (error) {
      console.error("Error fetching channel program data:", error);
      toast.error("Error fetching channel program data.");
    }
  };

  const handleSubmit = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      if (isEditMode) {
        await axios.put(`/channel_programs/${editChannelProgramId}`, formData, {
          headers,
        });
        toast.success("Channel Program updated successfully!");
      } else {
        await axios.post(`/channel_programs`, formData, { headers });
        toast.success("Channel Program created successfully!");
      }
      getChannelProgramList();
      resetForm();
    } catch (error) {
      console.error("Error creating/updating channel program:", error);
      toast.error(
        `Failed to ${isEditMode ? "update" : "create"} channel program.`
      );
    }
  };

  const handleEdit = (channelProgram) => {
    setIsEditMode(true);
    setEditChannelProgramId(channelProgram._id);
    setFormData({
      name: channelProgram.name,
    });
    document.getElementById("addChannelProgramModel").showModal();
  };

  const handleDelete = async (channelProgramId) => {
    if (
      !window.confirm("Are you sure you want to delete this channel program?")
    ) {
      return;
    }

    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      await axios.delete(`/channel_programs/${channelProgramId}`, { headers });
      toast.success("Channel Program deleted successfully!");
      getChannelProgramList();
    } catch (error) {
      console.error("Error deleting channel program:", error);
      toast.error("Failed to delete channel program.");
    }
  };

  const resetForm = () => {
    setFormData({ name: "" });
    setIsEditMode(false);
    setEditChannelProgramId(null);
    document.getElementById("addChannelProgramModel").close();
  };

  useEffect(() => {
    getChannelProgramList();
  }, [searchFilter]);

  return (
    <div className={"flex"}>
      <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold mb-4">Programms</h1>
          <div>
            <button
              onClick={() =>
                document.getElementById("addChannelProgramModel").showModal()
              }
              className="btn btn-primary mr-2"
              style={{ background: "limegreen", color: "white" }}
            >
              <span className="text-bold text-xl"> + </span> Add Program
            </button>
          </div>
        </div>

        {/* Creation/Edit Form */}
        <dialog id="addChannelProgramModel" className="modal">
          <div className="modal-box flex flex-col m-auto items-center p-6 rounded-lg shadow-md bg-white w-full max-w-2xl">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              {isEditMode ? "Edit Program" : "Create Program"}
            </h2>
            <div className="w-full mb-4">
              <input
                type="text"
                placeholder="Name"
                className="input input-bordered w-full mb-2"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>
            <div className="flex justify-center w-full mb-4 space-x-2">
              <button className="btn btn-primary" onClick={handleSubmit}>
                {isEditMode ? "Update" : "Create"}
              </button>

              <button
                className="btn btn-secondary"
                onClick={() => {
                  resetForm();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </dialog>

        <div className="p-4">
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Search by Name..."
            className="input input-bordered w-full"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {{
                        asc: " ↑",
                        desc: " ↓",
                      }[header.column.getIsSorted()] ?? null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="join p-4">
          <button
            className="join-item btn"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="join-item btn"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="join-item btn"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="join-item btn"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>

          <span
            style={{ alignItems: "center" }}
            className="flex input input-bordered join-item"
          >
            Page{" "}
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </span>
          <span className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={table.getPageCount()}
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="input input-bordered w-24"
            />
          </span>
          <select
            className="select select-bordered"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default ChannelProgramsPage;
